import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import Cookies from 'js-cookie';
import { ENV } from '../config/env';

const { FiUser, FiMail, FiCreditCard, FiAward, FiSettings, FiEdit3, FiLogOut, FiCheckCircle, FiLoader, FiMessageSquare, FiCode, FiDatabase, FiRefreshCw, FiChevronDown, FiChevronUp } = FiIcons;

const UserSettingsDashboard = ({ user, onLogout, sessionDebug }) => {
  const [profile, setProfile] = useState(user);
  const [isEditing, setIsEditing] = useState(false);
  const [tempUsername, setTempUsername] = useState(profile.username || '');
  const [saving, setSaving] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [showDebug, setShowDebug] = useState(false);

  const getCookieDomain = () => {
    const hostname = window.location.hostname;
    if (hostname.includes('deepsearchz.com')) {
      return '.deepsearchz.com';
    }
    return undefined;
  };

  const refreshProfile = async () => {
    if (!profile.user_id) return;
    setLoadingProfile(true);
    try {
      const res = await fetch(
        `${ENV.NCB_DATA_API}/read/users?Instance=${ENV.NCB_INSTANCE}&user_id=${profile.user_id}`,
        {
          headers: {
            'Authorization': `Bearer ${ENV.NCB_SECRET_KEY}`
          }
        }
      );
      const data = await res.json();
      if (data.data && data.data.length > 0) {
        const latestProfile = {
          ...profile,
          ...data.data[0],
          profile_id: data.data[0].id
        };
        setProfile(latestProfile);
        setTempUsername(latestProfile.username || '');
        
        // Update both localStorage and cookies
        localStorage.setItem('ds_profile', JSON.stringify(latestProfile));
        try {
          Cookies.set('ds_profile', JSON.stringify(latestProfile), {
            domain: getCookieDomain(),
            expires: 7,
            sameSite: 'Lax',
            secure: window.location.protocol === 'https:'
          });
        } catch (e) {
          console.log('[SESSION] Profile cookie update failed:', e);
        }
      }
    } catch (err) {
      console.error('[SESSION] Error fetching profile:', err);
    } finally {
      setLoadingProfile(false);
    }
  };

  useEffect(() => {
    refreshProfile();
  }, []);

  const handleUpdateProfile = async () => {
    if (!profile.profile_id) return;
    setSaving(true);
    try {
      const res = await fetch(
        `${ENV.NCB_DATA_API}/update/users/${profile.profile_id}?Instance=${ENV.NCB_INSTANCE}`,
        {
          method: "PUT",
          headers: {
            "Authorization": `Bearer ${ENV.NCB_SECRET_KEY}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            username: tempUsername,
            updated_at: new Date().toISOString().replace('T', ' ').split('.')[0]
          })
        }
      );
      if (res.ok) {
        await refreshProfile();
        setIsEditing(false);
      }
    } catch (err) {
      console.error("Update failed", err);
    } finally {
      setSaving(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await fetch(`${ENV.NCB_AUTH_API}/sign-out`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${ENV.NCB_SECRET_KEY}`,
          "X-Database-Instance": ENV.NCB_INSTANCE,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({})
      });
    } catch (err) {
      console.error('[SESSION] Sign-out API failed:', err);
    } finally {
      onLogout();
    }
  };

  const currentLimit = 10000;
  const progress = Math.min(((profile.credit || 0) / currentLimit) * 100, 100);
  const isFreeUser = profile.tier === 'Free';

  // Get current session data
  const getSessionData = () => {
    const localUser = localStorage.getItem('ds_user');
    const localProfile = localStorage.getItem('ds_profile');
    const cookieUser = Cookies.get('ds_user');
    const cookieProfile = Cookies.get('ds_profile');

    return {
      localStorage: {
        ds_user: localUser ? JSON.parse(localUser) : null,
        ds_profile: localProfile ? JSON.parse(localProfile) : null
      },
      cookies: {
        ds_user: cookieUser ? JSON.parse(cookieUser) : null,
        ds_profile: cookieProfile ? JSON.parse(cookieProfile) : null
      }
    };
  };

  const sessionData = getSessionData();

  return (
    <div className="min-h-screen bg-white text-black font-sans">
      <header className="border-b border-gray-100 py-6 sticky top-0 bg-white/80 backdrop-blur-md z-50">
        <div className="max-w-5xl mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 bg-black rounded-xl flex items-center justify-center overflow-hidden shadow-lg">
              <img src="https://i.imgur.com/lBD1NkT.png" alt="DeepSearchz" className="w-full h-full object-cover" />
            </div>
            <h1 className="text-xl font-black tracking-tighter uppercase tracking-widest text-xs">Terminal</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowDebug(!showDebug)}
              className="px-4 py-2 bg-blue-50 border-2 border-blue-100 rounded-xl hover:border-blue-500 transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2 text-blue-600"
            >
              <SafeIcon icon={FiCode} />
              {showDebug ? 'Hide' : 'Show'} Session
            </button>
            <button
              onClick={handleSignOut}
              className="px-4 py-2 border-2 border-gray-100 rounded-xl hover:border-black transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
            >
              Logout
              <SafeIcon icon={FiLogOut} />
            </button>
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-6 py-12">
        {showDebug && (
          <div className="mb-8 bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-[2rem] p-8 shadow-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiDatabase} className="text-lg" />
              </div>
              <div>
                <h2 className="text-lg font-black uppercase tracking-wider">Session Storage</h2>
                <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">
                  Dual Storage System (localStorage + Cookies)
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* localStorage */}
              <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                <h3 className="text-[12px] font-black uppercase tracking-wider mb-4 text-blue-400">📦 localStorage</h3>
                
                <div className="space-y-4">
                  <div className="bg-black/30 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-black uppercase text-gray-400">ds_user</span>
                      <span className={`text-[9px] font-bold px-2 py-1 rounded ${sessionData.localStorage.ds_user ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                        {sessionData.localStorage.ds_user ? '✓ SET' : '✗ EMPTY'}
                      </span>
                    </div>
                    {sessionData.localStorage.ds_user && (
                      <pre className="text-[8px] font-mono text-gray-300 overflow-x-auto mt-2 bg-black/50 p-2 rounded">
                        {JSON.stringify(sessionData.localStorage.ds_user, null, 2)}
                      </pre>
                    )}
                  </div>

                  <div className="bg-black/30 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-black uppercase text-gray-400">ds_profile</span>
                      <span className={`text-[9px] font-bold px-2 py-1 rounded ${sessionData.localStorage.ds_profile ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                        {sessionData.localStorage.ds_profile ? '✓ SET' : '✗ EMPTY'}
                      </span>
                    </div>
                    {sessionData.localStorage.ds_profile && (
                      <pre className="text-[8px] font-mono text-gray-300 overflow-x-auto mt-2 bg-black/50 p-2 rounded max-h-48">
                        {JSON.stringify(sessionData.localStorage.ds_profile, null, 2)}
                      </pre>
                    )}
                  </div>
                </div>
              </div>

              {/* Cookies */}
              <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                <h3 className="text-[12px] font-black uppercase tracking-wider mb-4 text-purple-400">🍪 Cookies</h3>
                
                <div className="space-y-4">
                  <div className="bg-black/30 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-black uppercase text-gray-400">ds_user</span>
                      <span className={`text-[9px] font-bold px-2 py-1 rounded ${sessionData.cookies.ds_user ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                        {sessionData.cookies.ds_user ? '✓ SET' : '✗ EMPTY'}
                      </span>
                    </div>
                    {sessionData.cookies.ds_user && (
                      <pre className="text-[8px] font-mono text-gray-300 overflow-x-auto mt-2 bg-black/50 p-2 rounded">
                        {JSON.stringify(sessionData.cookies.ds_user, null, 2)}
                      </pre>
                    )}
                  </div>

                  <div className="bg-black/30 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-black uppercase text-gray-400">ds_profile</span>
                      <span className={`text-[9px] font-bold px-2 py-1 rounded ${sessionData.cookies.ds_profile ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                        {sessionData.cookies.ds_profile ? '✓ SET' : '✗ EMPTY'}
                      </span>
                    </div>
                    {sessionData.cookies.ds_profile && (
                      <pre className="text-[8px] font-mono text-gray-300 overflow-x-auto mt-2 bg-black/50 p-2 rounded max-h-48">
                        {JSON.stringify(sessionData.cookies.ds_profile, null, 2)}
                      </pre>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
              <p className="text-[10px] text-blue-300 font-bold">
                💡 <strong>Dual Storage:</strong> Your session is saved in both localStorage (fast access) and cookies (cross-domain support). 
                On logout, both are cleared completely.
              </p>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <section className="bg-white border-2 border-gray-50 rounded-[2.5rem] p-8 shadow-sm">
              <h2 className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300 mb-8">Identity Module</h2>
              <div className="space-y-6">
                <div className="bg-gray-50/50 p-6 rounded-3xl group border-2 border-transparent hover:border-black transition-all">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 block mb-1">User Handle</span>
                      {isEditing ? (
                        <div className="flex gap-2">
                          <input
                            value={tempUsername}
                            onChange={e => setTempUsername(e.target.value)}
                            className="bg-white border-2 border-black rounded-xl px-4 py-2 text-sm font-black outline-none"
                          />
                          <button
                            onClick={handleUpdateProfile}
                            disabled={saving}
                            className="bg-black text-white px-4 rounded-xl text-[10px] font-black uppercase"
                          >
                            {saving ? <SafeIcon icon={FiLoader} className="animate-spin" /> : "Save"}
                          </button>
                        </div>
                      ) : (
                        <span className="text-lg font-black">{profile.username}</span>
                      )}
                    </div>
                    {!isEditing && (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="p-2 text-gray-300 hover:text-black transition-colors"
                      >
                        <SafeIcon icon={FiEdit3} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="bg-gray-50/50 p-6 rounded-3xl border-2 border-transparent">
                  <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 block mb-1">Authenticated Node</span>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-gray-600">{profile.email}</span>
                    <SafeIcon icon={FiCheckCircle} className="text-green-500 w-4 h-4" />
                  </div>
                </div>
              </div>
            </section>
            <section className="bg-black text-white rounded-[2.5rem] p-10 shadow-2xl overflow-hidden relative">
              <div className="relative z-10">
                <div className="flex justify-between items-center mb-10">
                  <h2 className="text-[10px] font-black uppercase tracking-[0.3em] opacity-50">Credit Balance</h2>
                  <span className="px-3 py-1 bg-white/10 rounded-full text-[8px] font-black uppercase tracking-widest">
                    {loadingProfile ? 'Syncing...' : 'Live Pool'}
                  </span>
                </div>
                <div className="flex items-baseline gap-3 mb-6">
                  <span className="text-6xl font-black tracking-tighter">{(profile.credit || 0).toLocaleString()}</span>
                  <span className="text-xs font-bold opacity-30 tracking-widest uppercase">N_CR</span>
                </div>
                <div className="w-full bg-white/10 h-2 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    className="bg-white h-full rounded-full"
                  />
                </div>
              </div>
            </section>
          </div>
          <div className="space-y-8">
            <section className="bg-gray-50 rounded-[2rem] p-8 border-2 border-transparent hover:border-black transition-all">
              <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center mb-6 shadow-lg overflow-hidden">
                <img src="https://i.imgur.com/lBD1NkT.png" alt="DeepSearchz" className="w-full h-full object-cover" />
              </div>
              <h3 className="text-4xl font-black tracking-tighter mb-2">{profile.tier || 'Free'}</h3>
              <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Clearance Status</p>
            </section>
            {isFreeUser && (
              <div className="bg-amber-50 border-2 border-amber-200 rounded-[2rem] p-6">
                <div className="flex items-start gap-3 mb-4">
                  <SafeIcon icon={FiMessageSquare} className="text-amber-600 mt-1 flex-shrink-0 text-lg" />
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-900 mb-2">
                      Limited Access
                    </p>
                    <p className="text-[9px] text-amber-800 leading-relaxed">
                      This application is currently in private beta with limited features. To unlock full access and use the app, please contact the owner for a premium plan.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserSettingsDashboard;